// nameEngine.js — katakana/"Engrish" consonant-confusion name engine.
// Derived from the 1994 Japanese baseball roster naming style: preserve length
// and rhythm, change (usually) a single onset consonant from a fixed confusion
// set. Two presets share the core; this file ships the GAME preset (subtle,
// collision-safe) used by Starting Five. The funnier generator preset can reuse
// the same primitives with `safe=false` and a looser edit budget.

import { ICONS, STARS, UNCL } from "./sfData";

// ---- confusion tables ----
const SUB = {
  B: ["D", "V", "P", "G"], C: ["G", "K"], D: ["B", "T", "G"], F: ["P", "H"],
  G: ["K", "J", "C", "B"], H: ["N"], J: ["G", "R", "Z"], K: ["C", "G", "T"],
  L: ["R", "N"], M: ["N", "B", "W"], N: ["M", "L", "R"], P: ["B", "F", "T"],
  R: ["L", "N", "G", "D"], S: ["K", "T", "Z", "N"], T: ["D", "L", "P"],
  V: ["B", "W", "F"], W: ["M", "V", "G"], Z: ["S", "J"],
};
const DIG = { Th: ["T", "S", "D"], Ch: ["K", "T"], Sh: ["S"], Ph: ["P", "F"], Wh: ["W", "V"] };
const VOW = { a: "eo", e: "ia", i: "oe", o: "ua", u: "ao" };
// subtle, plausible swaps for the game preset
const SAFE = {
  R: "L", L: "R", M: "N", N: "M", B: "D", D: "B", C: "G", G: "C",
  S: "K", K: "C", T: "D", P: "B", W: "M", V: "B", F: "P",
};

const COMMON_FIRST = new Set(["Mike","John","Dave","David","Jim","Bob","Bill","Tom","Steve","Chris","Mark","Paul","Kevin","Joe","Tim","Rob","Dan","Jeff"]);
const COMMON_LAST = new Set(["Smith","Johnson","Williams","Jones","Brown","Davis","Miller","Wilson","Moore","Taylor","Anderson","Thomas","Jackson","White","Harris","Martin","Clark","Lewis","Walker","Hall","Allen","Young","King","Scott","Green","Baker"]);

// ---- seeded RNG (mulberry32) so boards can be reproducible / daily ----
export function makeRng(seed) {
  let s = seed >>> 0;
  return function () {
    s |= 0; s = (s + 0x6d2b79f5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
const pick = (arr, rng) => arr[Math.floor(rng() * arr.length)];

// ---- name helpers ----
function splitName(full) {
  const n = String(full).replace(/\s+(Jr\.?|Sr\.?|II|III|IV|V)$/, "").trim();
  const p = n.split(/\s+/);
  return p.length > 1 ? { first: p[0], last: p.slice(1).join(" ") } : { first: p[0], last: "" };
}
function keepCase(orig, repl) {
  if (orig === orig.toUpperCase() && orig.length === 1) return repl.toUpperCase();
  if (orig[0] === orig[0].toUpperCase()) return repl[0].toUpperCase() + repl.slice(1).toLowerCase();
  return repl.toLowerCase();
}
const clusterLen = (s) => { const m = s.match(/^[^aeiouAEIOU]+/); return m ? m[0].length : 0; };
function preSplit(last) {
  for (const p of ["Mc", "Mac", "O'"]) if (last.startsWith(p) && last.length > p.length + 1) return [p, last.slice(p.length)];
  return ["", last];
}
function subOnset(stem, safe, rng) {
  for (const d in DIG) if (stem.slice(0, 2).replace(/^./, (c) => c.toUpperCase()).replace(/.$/, (c) => c.toLowerCase()) === d)
    return [keepCase(stem.slice(0, 2), pick(DIG[d], rng)) + stem.slice(2), true];
  const c = stem[0].toUpperCase();
  if (safe) return c in SAFE ? [keepCase(stem[0], SAFE[c]) + stem.slice(1), true] : [stem, false];
  return c in SUB ? [keepCase(stem[0], pick(SUB[c], rng)) + stem.slice(1), true] : [stem, false];
}
function subInterior(stem, safe, rng) {
  const idx = [];
  for (let i = 1; i < stem.length; i++) if (stem[i].toUpperCase() in SUB) idx.push(i);
  if (!idx.length) return [stem, false];
  const i = pick(idx, rng), c = stem[i].toUpperCase();
  const pool = safe && c in SAFE ? [SAFE[c]] : SUB[c];
  return [stem.slice(0, i) + keepCase(stem[i], pick(pool, rng)) + stem.slice(i + 1), true];
}
function subVowel(stem, rng) {
  const idx = [];
  for (let i = 0; i < stem.length; i++) if (stem[i].toLowerCase() in VOW) idx.push(i);
  if (!idx.length) return [stem, false];
  const i = pick(idx, rng), v = stem[i].toLowerCase();
  return [stem.slice(0, i) + keepCase(stem[i], pick(VOW[v].split(""), rng)) + stem.slice(i + 1), true];
}
function ugly(last) {
  if (/[^aeiouAEIOU]{3}/.test(last)) return true;
  if (/^(Vr|Kh|Zl|Tk|Pm|Bm|Mt|Nk|Kt|Dl|Gd|Wn|Ln|Rn|Nl|Ml|Gn|Bn|Fn|Hn|Vn|Tl|Pn|Dn|Tn|Bd|Db|Kn|Nb|Nm|Mn|Lm|Lr|Rl)/.test(last)) return true;
  if (/(Sd|Sc|Zd|Kd|Td|Pd)$/.test(last)) return true;
  return false;
}
function mutateLast(last, rng) {
  const [pre, stem] = preSplit(last);
  const hasCluster = clusterLen(stem) > 1;
  for (let t = 0; t < 14; t++) {
    let s = stem, ok = false, r = rng();
    if (r < 0.7 && !hasCluster) [s, ok] = subOnset(s, true, rng);
    else if (r < 0.9) [s, ok] = subInterior(s, true, rng);
    else [s, ok] = subVowel(s, rng);
    if (!ok) [s, ok] = subInterior(s, true, rng);
    if (!ok) [s, ok] = subOnset(s, true, rng);
    const cand = pre + s;
    if (cand !== last && !ugly(cand)) return cand;
  }
  return pre + stem;
}

// ---- data prep (once) ----
let DATA = null;
export function prepData() {
  if (DATA) return DATA;
  const parse = (arr) => arr.map(splitName).filter((x) => x.last);
  const iconP = parse(ICONS), starP = parse(STARS), unclP = parse(UNCL);
  const realSet = new Set([...ICONS, ...STARS, ...UNCL].map((n) => String(n).trim()));
  const realLast = new Set([...iconP, ...starP, ...unclP].map((x) => x.last));
  DATA = { iconP, starP, unclP, realSet, realLast };
  return DATA;
}

export function makeGameFake(rng, data = prepData(), pool = null) {
  const pairs = pool || data.unclP;
  let last, first, nl;
  for (let t = 0; t < 60; t++) {
    const src = pick(pairs, rng); first = src.first; last = src.last;
    nl = mutateLast(last, rng);
    const full = `${first} ${nl}`;
    if (data.realSet.has(full)) continue;
    if (data.realLast.has(nl)) continue;
    if (COMMON_FIRST.has(first) && COMMON_LAST.has(nl)) continue;
    return { fake: full, source: `${first} ${last}`, sourceLast: last };
  }
  return { fake: `${first} ${nl}`, source: `${first} ${last}`, sourceLast: last };
}

// ---- board: 15 tiles, 5 fake (2 icons, 3 stars, 5 obscure reals + 5 fakes) ----
export function generateBoard(seed, opts = {}) {
  const data = prepData();
  const rng = seed == null ? Math.random : makeRng(seed);
  const nIcon = opts.icons ?? 2, nStar = opts.stars ?? 3, nUncl = opts.uncl ?? 5, nFake = opts.fakes ?? 5;
  const sample = (arr, k) => {
    const a = arr.slice(); const out = [];
    for (let i = 0; i < k && a.length; i++) out.push(a.splice(Math.floor(rng() * a.length), 1)[0]);
    return out;
  };
  const reals = [
    ...sample(data.iconP, nIcon).map((x) => ({ name: `${x.first} ${x.last}`, isFake: false, tier: "icon" })),
    ...sample(data.starP, nStar).map((x) => ({ name: `${x.first} ${x.last}`, isFake: false, tier: "star" })),
    ...sample(data.unclP, nUncl).map((x) => ({ name: `${x.first} ${x.last}`, isFake: false, tier: "uncl" })),
  ];
  const fakes = []; const seen = new Set(reals.map((r) => r.name));
  let guard = 0;
  while (fakes.length < nFake && guard++ < 400) {
    const f = makeGameFake(rng, data);
    if (seen.has(f.fake)) continue;
    seen.add(f.fake);
    fakes.push({ name: f.fake, isFake: true, source: f.source, sourceLast: f.sourceLast });
  }
  const tiles = [...reals, ...fakes];
  for (let i = tiles.length - 1; i > 0; i--) { const j = Math.floor(rng() * (i + 1)); [tiles[i], tiles[j]] = [tiles[j], tiles[i]]; }
  return tiles.map((t, i) => ({ id: i, ...t }));
}

// ═══════════════════════════════════════════════════════════════════
// GENERATOR PRESET — looser, funnier edit budget for the landing page.
// Reuses the same katakana primitives with safe=false so swaps come from
// the full SUB table (Bobson-Dugnutt energy) instead of the subtle SAFE
// set. Returns the shape App.js already renders:
//   { name, initials, fullChaos, sources: [{name, pct} x3] }
// ═══════════════════════════════════════════════════════════════════

// FNV-1a string hash → deterministic seed from free-typed input.
export function hashStr(str) {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) { h ^= str.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  return h >>> 0;
}

// Aggressive last-name mutation for the generator: 1–2 edits, unsafe pool,
// still guarded by ugly() so output stays pronounceable.
function mutateLastLoose(last, rng) {
  const [pre, stem0] = preSplit(last);
  for (let t = 0; t < 18; t++) {
    let s = stem0, edits = 0;
    const budget = rng() < 0.45 ? 2 : 1; // ~45% get a double mutation
    for (let e = 0; e < budget; e++) {
      const r = rng();
      let ok = false;
      if (r < 0.55) [s, ok] = subOnset(s, false, rng);
      else if (r < 0.85) [s, ok] = subInterior(s, false, rng);
      else [s, ok] = subVowel(s, rng);
      if (ok) edits++;
    }
    if (!edits) [s, ] = subInterior(s, false, rng);
    const cand = pre + s;
    if (cand !== last && !ugly(cand)) return cand;
  }
  // last resort: prefer a non-ugly interior/vowel edit before an onset swap
  for (const fn of [subInterior, subVowel]) {
    const [s, ok] = fn === subVowel ? fn(stem0, rng) : fn(stem0, false, rng);
    const cand = pre + s;
    if (ok && cand !== last && !ugly(cand)) return cand;
  }
  const [f] = subOnset(stem0, false, rng);
  const forced = pre + f;
  return ugly(forced) ? last : forced; // never emit an ugly onset; keep original if stuck
}

// Subtle-ish first-name garble for the generator (keeps it readable).
function mutateFirstLoose(first, rng) {
  if (first.length < 3) return first;
  const r = rng();
  if (r < 0.5) { const [s, ok] = subInterior(first, false, rng); return ok ? s : first; }
  if (r < 0.8) { const [s, ok] = subVowel(first, rng); return ok ? s : first; }
  const [s, ok] = subOnset(first, false, rng);
  return ok ? s : first;
}

export function makeGeneratorName(input, reroll = 0, data = prepData()) {
  const seed = (hashStr(String(input).toLowerCase().trim() + "|" + reroll)) >>> 0;
  const rng = makeRng(seed);
  const parts = String(input).trim().split(/\s+/);
  const inputFirst = parts[0] || String(input).trim() || "Player";

  // ~8% "full chaos": ignore the input initial entirely.
  const fullChaos = rng() < 0.08;

  // Source athletes power the aura panel and (for chaos) the output name.
  const allPairs = [...data.iconP, ...data.starP, ...data.unclP];
  const byFirstInitial = (ch) => {
    const c = ch.toUpperCase();
    const hits = allPairs.filter((p) => p.first[0].toUpperCase() === c);
    return hits.length ? hits : allPairs;
  };

  // a1 seeds the first name, a2 the last name, a3 flavors the aura.
  const a1 = fullChaos ? pick(allPairs, rng) : pick(byFirstInitial(inputFirst[0] || "A"), rng);
  let a2 = pick(allPairs, rng), a3 = pick(allPairs, rng);
  let g = 0; while (a2 === a1 && g++ < 20) a2 = pick(allPairs, rng);
  g = 0; while ((a3 === a1 || a3 === a2) && g++ < 20) a3 = pick(allPairs, rng);

  // First name: for non-chaos, keep the user's own first name (garbled a bit);
  // for chaos, borrow a1's first name.
  let firstName = fullChaos ? a1.first : inputFirst;
  firstName = firstName[0].toUpperCase() + firstName.slice(1).toLowerCase();
  if (rng() < 0.4) firstName = mutateFirstLoose(firstName, rng);

  // Last name: always mutated (that's the whole point).
  let lastName = mutateLastLoose(a2.last, rng);

  // Enforce input initial on the first name unless chaos.
  if (!fullChaos && firstName[0].toUpperCase() !== (inputFirst[0] || "A").toUpperCase()) {
    firstName = (inputFirst[0] || "A").toUpperCase() + firstName.slice(1);
  }

  // Occasional suffix (~5%).
  let suffix = "";
  const sr = rng();
  if (sr < 0.02) suffix = " III"; else if (sr < 0.035) suffix = " II"; else if (sr < 0.05) suffix = " Jr.";

  firstName = firstName[0].toUpperCase() + firstName.slice(1);
  lastName = lastName[0].toUpperCase() + lastName.slice(1);

  // Aura percentages (deterministic from seed).
  const p1 = 35 + (seed % 25);
  const p2 = 25 + ((seed >>> 5) % 20);
  const p3 = Math.max(5, 100 - p1 - p2);

  return {
    name: `${firstName} ${lastName}${suffix}`,
    initials: `${firstName[0]}.${lastName[0]}.`,
    fullChaos,
    sources: [
      { name: `${a1.first} ${a1.last}`, pct: p1 },
      { name: `${a2.first} ${a2.last}`, pct: p2 },
      { name: `${a3.first} ${a3.last}`, pct: p3 },
    ],
  };
}

// ═══════════════════════════════════════════════════════════════════
// DAILY MODE helpers.
// ═══════════════════════════════════════════════════════════════════

// Stable YYYY-MM-DD in *local* time (so "today's puzzle" matches the player's
// calendar day, not UTC).
export function dailyKey(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// Deterministic 32-bit seed for a given day. Everyone gets the same board.
export function dailySeed(d = new Date()) {
  return hashStr("buttfumbles-daily|" + dailyKey(d)) >>> 0;
}

// Sub-tier the flat Unclassified pool into two stable bands so daily boards
// draw a controlled recognizable:obscure ratio instead of swinging randomly.
// NOTE: sfData ships UNCL as one undifferentiated bucket, so this split is by
// a stable per-name hash, not a true fame ranking — it fixes the *variance*
// (every board gets the same mix) even though the bands aren't semantic.
// If a real recognizability score is added to the data later, swap the
// predicate here and the rest of daily mode keeps working.
function bandOf(pair) {
  return (hashStr(pair.first + " " + pair.last) % 100) < 45 ? "near" : "deep";
}
let BANDS = null;
function subtierUncl(data) {
  if (BANDS) return BANDS;
  const near = [], deep = [];
  for (const p of data.unclP) (bandOf(p) === "near" ? near : deep).push(p);
  BANDS = { near, deep };
  return BANDS;
}

// Daily board: same as generateBoard but the Unclassified reals are drawn with
// a fixed near/deep ratio for even difficulty. Uses the day's seed by default.
export function generateDailyBoard(d = new Date(), opts = {}) {
  const data = prepData();
  const seed = dailySeed(d);
  const rng = makeRng(seed);
  const nIcon = opts.icons ?? 2, nStar = opts.stars ?? 3, nUncl = opts.uncl ?? 5, nFake = opts.fakes ?? 5;
  const nNear = opts.near ?? 3; // of the 5 obscure reals, 3 from the "near" band
  const { near, deep } = subtierUncl(data);

  const sample = (arr, k) => {
    const a = arr.slice(), out = [];
    for (let i = 0; i < k && a.length; i++) out.push(a.splice(Math.floor(rng() * a.length), 1)[0]);
    return out;
  };

  const unclPicks = [
    ...sample(near, Math.min(nNear, nUncl)),
    ...sample(deep, nUncl - Math.min(nNear, nUncl)),
  ];

  const reals = [
    ...sample(data.iconP, nIcon).map((x) => ({ name: `${x.first} ${x.last}`, isFake: false, tier: "icon" })),
    ...sample(data.starP, nStar).map((x) => ({ name: `${x.first} ${x.last}`, isFake: false, tier: "star" })),
    ...unclPicks.map((x) => ({ name: `${x.first} ${x.last}`, isFake: false, tier: "uncl" })),
  ];

  const fakes = []; const seen = new Set(reals.map((r) => r.name));
  let guard = 0;
  while (fakes.length < nFake && guard++ < 400) {
    const f = makeGameFake(rng, data);
    if (seen.has(f.fake)) continue;
    seen.add(f.fake);
    fakes.push({ name: f.fake, isFake: true, source: f.source, sourceLast: f.sourceLast });
  }

  const tiles = [...reals, ...fakes];
  for (let i = tiles.length - 1; i > 0; i--) { const j = Math.floor(rng() * (i + 1)); [tiles[i], tiles[j]] = [tiles[j], tiles[i]]; }
  return tiles.map((t, i) => ({ id: i, ...t }));
}
